import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';
import { useEffect, useState } from 'react';
import { personService } from '@shared/services/personService';
import { useNavigate } from 'react-router-dom';
import CustomDatePicker from '@shared/components/CustomDatePicker';

const WorkingCapacityForm = ({ onClose, onConfirm, editData = null, personId }) => {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    

    const validationSchema = yup.object({
        fromDate: yup.string()
            .required(t('validation.person.workingCapacity.dateRequired')),
        toDate: yup.string()
            .when(['isLifelong'], {
                is: (isLifelong) => !isLifelong,
                then: () => yup.string().required(t('validation.person.workingCapacity.dateRequired')),
                otherwise: () => yup.string().notRequired()
            }),
        percent: yup.number()
            .required(t('validation.person.workingCapacity.percentRequired'))
            .typeError(t('validation.person.workingCapacity.percentRequired')),
        // docId: yup.string()
        //     .required(t('validation.person.workingCapacity.docIdRequired')),
        orderNr: yup.string()
            .required(t('validation.person.workingCapacity.orderNrRequired')),
        orderDate: yup.string()
            .required(t('validation.person.workingCapacity.orderDateRequired')),
        isLifelong: yup.boolean()
    });

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setValue
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            fromDate: editData?.fromDate || '',
            toDate: editData?.toDate || '',
            percent: editData?.percent?.toString() || '',
            docId: editData?.docId?.toString() || '',
            orderNr: editData?.orderNr || '',
            orderDate: editData?.orderDate || '',
            isLifelong: editData?.toDate === null
        }
    });

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                setLoading(true);
                const response = await personService.getSystemDocuments(personId);
                setFiles(response);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching files:', error);
                setLoading(false);
            }
        };

        fetchFiles();
    }, [personId]);

    const isLifelong = watch('isLifelong');
    const isEdit = Boolean(editData);

    const onFormSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit((data) => {
            const selectedFile = files.find(file => file.id.toString() === data.docId);
            
            const formattedData = {
                fromDate: data.fromDate,
                toDate: data.isLifelong ? null : data.toDate,
                percent: parseInt(data.percent),
                docId: parseInt(data.docId),
                orderNr: data.orderNr,
                orderDate: data.orderDate
            };
        
            onConfirm(formattedData);
        })(e);
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {isEdit ? t('persons.workingCapacity.editCapacity') : t('persons.workingCapacity.addCapacity')}
                        </h4>
                        <form id="workingCapacityForm" className="region-form" onSubmit={onFormSubmit}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="input-checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="isLifelong"
                                            {...register('isLifelong')}
                                        />
                                        <span className="form-check-label span-bold" htmlFor="isLifelong">
                                            {t('persons.workingCapacity.lifeLong')}
                                        </span>
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.workingCapacity.from')} <span className="required">*</span>
                                        </span>
                                        <CustomDatePicker
                                            name="fromDate"
                                            value={watch('fromDate')}
                                            onChange={(date) => setValue('fromDate', date, { shouldValidate: true })}
                                            errors={errors}
                                            placeholderText={t('common.dateFormat')}
                                            maxDate={new Date()}
                                        />
                                    </div>

                                    {!isLifelong ? (
                                        <div className="form-group">
                                            <span className="span-bold">
                                                {t('persons.workingCapacity.endDate')} <span className="required">*</span>
                                            </span>
                                            <CustomDatePicker
                                                name="toDate"
                                                value={watch('toDate')}
                                                onChange={(date) => setValue('toDate', date, { shouldValidate: true })}
                                                errors={errors}
                                                placeholderText={t('common.dateFormat')}
                                                maxDate={new Date()}
                                            />
                                        </div>
                                    ) : (
                                        <div className="form-group">
                                            <span className="span-bold">
                                                {t('persons.workingCapacity.endDate')}
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control"
                                                disabled
                                                value=""
                                            />
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.workingCapacity.percentage')} <span className="required">*</span>
                                        </span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.percent ? 'is-invalid' : ''}`}
                                            placeholder={t('persons.workingCapacity.enterPercentage')}
                                            {...register('percent')}
                                        />
                                        {errors.percent && (
                                            <div className="invalid-feedback">{errors.percent.message}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.workingCapacity.documentAttached')}
                                        </span>
                                        {loading ? (
                                            <div>{t('persons.workingCapacity.loadingDocuments')}</div>
                                        ) : (
                                            <select
                                                className={`form-control ${errors.docId ? 'is-invalid' : ''}`}
                                                {...register('docId')}
                                            >
                                                <option value="">{t('persons.workingCapacity.chooseDocument')}</option>
                                                {files.map(file => (
                                                    <option key={file.id} value={file.id}>
                                                        {file.empdoctype}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {errors.docId && (
                                            <div className="invalid-feedback">{errors.docId.message}</div>
                                        )}
                                        <button type="button" className="btn btn-info" onClick={() => navigate(`/system-documents/${personId}`)}>
                                            {t('common.viewDocuments')}
                                        </button>
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.workingCapacity.readjustmentOrder')} <span className="required">*</span>
                                        </span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.orderNr ? 'is-invalid' : ''}`}
                                            placeholder={t('persons.workingCapacity.enterOrderNumber')}
                                            {...register('orderNr')}
                                        />
                                        {errors.orderNr && (
                                            <div className="invalid-feedback">{errors.orderNr.message}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.workingCapacity.dateOfOrder')} <span className="required">*</span>
                                        </span>
                                        <CustomDatePicker
                                            name="orderDate"
                                            value={watch('orderDate')}
                                            onChange={(date) => setValue('orderDate', date, { shouldValidate: true })}
                                            errors={errors}
                                            placeholderText={t('common.dateFormat')}
                                            maxDate={new Date()}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="workingCapacityForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkingCapacityForm;